import ActionNodeTemplate from "../templates/ActionNodeTemplate";
import { Tooltip, Typography, Box, IconButton } from "@mui/material";
import { Handle } from "@xyflow/react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useState, useContext } from "react";
import nodeTypesConfig from "../../../../models/nodeTypesConfig";
import NodeConfigModal from "../../menus/NodeConfigModal";
import { useThemeContext } from "../../../../Theme/Theme";
import AddNodeContext from "../../../../contexts/AddNodeContext";

export default function CustomCommandNode({ id, type, data, selected }) {
  const [configModal, setConfigModal] = useState({ open: false });
  const { mode } = useThemeContext();
  const { onNodesDelete, setSelectedNode } = useContext(AddNodeContext);

  const handleEdit = (e) => {
    e.stopPropagation();

    if (setSelectedNode) {
      setSelectedNode({ id, type, data, selected });
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onNodesDelete([{ id }]);
  };

  const handleContentClick = (e) => {
    e.stopPropagation();
  };

  // Helper function to format pipe conditions
  const formatPipeConditions = () => {
    // Check multiple possible field names for pipe conditions
    const pipeData =
      data.pipeConditions ||
      data.pipes ||
      data.queryConditions ||
      data.conditions;

    if (!Array.isArray(pipeData) || pipeData.length === 0) {
      return "No pipe conditions set";
    }

    // Group by logical operator
    const andConditions = pipeData.filter((c) => c.logicalOperator === "AND");
    const orConditions = pipeData.filter((c) => c.logicalOperator === "OR");

    const formatCondition = (condition) => {
      if (!condition.table || !condition.field || !condition.operator)
        return "";

      const op = String(condition.operator).toLowerCase();
      let val = condition.value;

      if (
        typeof val === "string" &&
        op.includes("between") &&
        val.includes("|")
      ) {
        const [a = "", b = ""] = val.split("|");
        val = `${a} - ${b}`;
      }

      return `${condition.table}.${condition.field} ${condition.operator} ${val}`;
    };

    let result = "";
    if (andConditions.length > 0) {
      result += andConditions
        .map(formatCondition)
        .filter(Boolean)
        .join(" AND ");
    }
    if (orConditions.length > 0) {
      if (result) result += " OR ";
      result += orConditions.map(formatCondition).filter(Boolean).join(" OR ");
    }

    return result || "Pipe conditions configured";
  };

  // Get icon from config
  const nodeConfig = nodeTypesConfig.actions.find((node) => node.type === type);
  const IconComponent = nodeConfig?.icon || null;
  const iconColor = nodeConfig?.iconColor || "#e0e0e0";

  return (
    <>
      <div
        className={`node-template ${mode}`}
        style={{
          minWidth: 150,
          maxWidth: 150,
          width: "100%",
          minHeight: 50,
          maxHeight: 50,
          height: "100%",
          borderRadius: 10,
        }}
        onClick={handleContentClick}
      >
        <Box sx={{ marginLeft: -1, marginTop: 0.3, marginRight: -1 }}>
          {/* Header (label + edit icon) */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid #444",
              width: "100%",
              marginTop: -18,
            }}
          >
            <Tooltip
              title={data.command?.label || data.label || "Custom Command"}
              placement="top"
            >
              <strong style={{ color: "#00aaff", fontSize: 8 }}>
                {data.command?.label || data.label || "Custom Command"}
              </strong>
            </Tooltip>
            <div style={{ display: "flex", gap: 1 }}>
              <Tooltip title="Edit" placement="top">
                <IconButton size="small" onClick={handleEdit}>
                  <EditIcon sx={{ color: "#aaa", fontSize: 10 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete" placement="top">
                <IconButton size="small" onClick={handleDelete}>
                  <DeleteIcon sx={{ color: "#aaa", fontSize: 10 }} />
                </IconButton>
              </Tooltip>
            </div>
          </div>

          {/* Condition text + icon row */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* The pipe conditions string in a Tooltip for longer text */}
            <Tooltip title={formatPipeConditions()}>
              <Typography
                noWrap
                fontSize={8}
                sx={{
                  flex: 1,
                  lineHeight: "1.4",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {formatPipeConditions()}
              </Typography>
            </Tooltip>

            {/* Icon */}
            {IconComponent && (
              <Box
                sx={{
                  backgroundColor: iconColor,
                  borderRadius: "50%",
                  width: 18,
                  height: 18,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  mt: 0.5,
                  color: "white",
                  position: "relative",
                  lineHeight: 0,
                }}
              >
                <IconComponent
                  sx={{ width: "65%", height: "75%" }}
                  width={13}
                  height={13}
                  mb={0.1}
                />
              </Box>
            )}
          </div>
        </Box>

        {/* React Flow Handles - Two output handles like ConditionNode */}
        <Handle
          type="target"
          position="top"
          style={{ background: "#00aaff" }}
        />
        <Handle
          type="source"
          position="bottom"
          id="match"
          style={{ left: 20, background: "#00aaff" }}
        />
        <Handle
          type="source"
          position="bottom"
          id="nomatch"
          style={{ left: 130, background: "#00aaff" }}
        />
      </div>

      {configModal.open && (
        <NodeConfigModal
          open={configModal.open}
          onClose={() => setConfigModal({ open: false })}
        >
          {configModal.content}
        </NodeConfigModal>
      )}
    </>
  );
}
